import { useState } from 'react';
import { maSignatureApi } from '../api/resources';
import { useSession } from '../api/session.jsx';
import { MaSignatureCard } from '../features/reglages/MaSignatureCard.jsx';
import { C } from '../theme/palette';

/**
 * « Ramener la signature » : pose la signature enregistrée de la personne
 * connectée — patron, secrétaire, technicien — au lieu de la redessiner.
 * Sans signature enregistrée, le patron retrouve celle des documents
 * (Réglages → Documents).
 *
 * @param {object} props propriétés du composant
 * @param {(image: string) => void} props.onUtilise reçoit l'image PNG encodée
 * @param {object} [props.soc] société, pour la signature du patron des documents
 * @returns {JSX.Element} le bouton et son message
 */
export function BoutonMaSignature({ onUtilise, soc }) {
  const { session } = useSession();
  const [etat, setEtat] = useState({ enCours: false, message: '' });
  // Sa signature se dessine ici, une fois, puis se garde pour ses prochains
  // courriers et décharges : le patron comme le bureau (qui n'a pas les
  // Réglages).
  const [aDessiner, setADessiner] = useState(false);

  const utilise = async () => {
    setEtat({ enCours: true, message: '' });
    try {
      const r = await maSignatureApi.lit();
      const image = r?.signature || (session?.role === 'patron' ? soc?.sigPatron : '') || '';
      if (image) {
        onUtilise(image);
        setEtat({ enCours: false, message: '' });
      } else if (session?.role) {
        setADessiner(true);
        setEtat({
          enCours: false,
          message:
            'Aucune signature enregistrée : dessinez-la une fois ci-dessous, elle sera gardée pour vos prochains courriers et décharges.',
        });
      } else {
        setEtat({
          enCours: false,
          message:
            'Aucune signature enregistrée : dessinez-la une fois dans Réglages → Mon compte → Ma signature.',
        });
      }
    } catch (e) {
      setEtat({ enCours: false, message: e?.message || 'La signature n’a pas pu être lue.' });
    }
  };

  return (
    <div style={{ marginBottom: 6 }}>
      <button
        type="button"
        onClick={etat.enCours ? undefined : utilise}
        style={{
          border: '1px solid ' + C.navy,
          background: C.white,
          color: C.navy,
          borderRadius: 8,
          padding: '8px 10px',
          fontSize: 10.5,
          fontWeight: 800,
          cursor: 'pointer',
          width: '100%',
        }}
      >
        {etat.enCours ? '…' : '✒️ Utiliser ma signature enregistrée'}
      </button>
      {etat.message && (
        <div role="status" style={{ fontSize: 9.5, color: C.amber, marginTop: 4, lineHeight: 1.4 }}>
          {etat.message}
        </div>
      )}
      {aDessiner && (
        <div style={{ marginTop: 6 }}>
          <MaSignatureCard
            onEnregistre={(image) => {
              setADessiner(false);
              setEtat({ enCours: false, message: '' });
              onUtilise(image);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Vrai pour une signature posée en image (signature enregistrée), faux pour un tracé. */
export const estUneImage = (signature) => /^data:image\//.test(String(signature || ''));
